import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { API_URL } from '../utils/api';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [authToken, setAuthToken] = useState(() => localStorage.getItem('authToken'));
    const [refreshToken, setRefreshToken] = useState(() => localStorage.getItem('refreshToken'));
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const decodeToken = (token) => {
        if (!token) return null;
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            // Check if token is expired or will expire in the next 1 minute
            if (payload.exp * 1000 < (Date.now() + 60000)) {
                console.warn("Auth token expired or near expiration");
                return null;
            }
            const userData = {
                username: payload.username || payload.sub || null,
                user_id: payload.user_id || payload.pk || null,
                first_name: payload.first_name || null,
                is_staff: payload.is_staff || false,
                is_superuser: payload.is_superuser || false,
                can_manage_images: payload.can_manage_images || false,
                is_guest: payload.is_guest || (payload.username && (payload.username.toLowerCase().startsWith('guest_') || payload.username.toLowerCase() === 'gast')) || false
            };
            
            return userData;
        } catch (e) {
            console.error("Invalid token:", e);
            return null;
        }
    };

    useEffect(() => {
        const currentUser = decodeToken(authToken);
        setUser(currentUser);
        setLoading(false); // Initial check done
    }, [authToken]);

    const login = (access, refresh) => {
        localStorage.setItem('authToken', access);
        localStorage.setItem('refreshToken', refresh);
        setAuthToken(access);
        setRefreshToken(refresh);
    };

    const logout = useCallback(() => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        setAuthToken(null);
        setRefreshToken(null);
        setUser(null);

        // Dispatch custom event for navigation
        window.dispatchEvent(new CustomEvent('auth:logout'));
    }, []);

    const refreshAuthToken = useCallback(async () => {
        const currentRefreshToken = localStorage.getItem('refreshToken');
        if (!currentRefreshToken) return logout();

        try {
            // Note: API_URL might already have a trailing slash or not. 
            // In utils/api.js it is typically defined without slash if we append /api/...
            // The original code used `${API_URL}/api/token/refresh/`.
            const response = await fetch(`${API_URL}/api/token/refresh/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh: currentRefreshToken })
            });
            if (!response.ok) throw new Error('Token refresh failed');
            const data = await response.json();
            login(data.access, currentRefreshToken); // Update with new access token
            return data.access;
        } catch (error) {
            console.error('Failed to refresh token:', error);
            logout(); // Log out if refresh fails
            return null;
        }
    }, []);

    const getCookie = (name) => {
        let cookieValue = null;
        if (document.cookie && document.cookie !== '') {
            const cookies = document.cookie.split(';');
            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i].trim();
                if (cookie.substring(0, name.length + 1) === (name + '=')) {
                    cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                    break;
                }
            }
        }
        return cookieValue;
    };

    const fetchWithAuth = useCallback(async (urlPath, options = {}) => {
        let currentAuthToken = localStorage.getItem('authToken');
        let currentUser = decodeToken(currentAuthToken);

        if (!currentUser) {
            currentAuthToken = await refreshAuthToken();
            if (!currentAuthToken) {
                throw new Error("Authentication required.");
            }
        }

        // Handle URL. distinct if urlPath starts with http
        let urlStr = urlPath.startsWith('http') ? urlPath : `${API_URL}${urlPath}`;


        const headers = {
            ...options.headers,
            'Authorization': `Bearer ${currentAuthToken}`,
        };

        // Add CSRF token only for non-safe methods (important if user has a session cookie)
        const safeMethods = ['GET', 'HEAD', 'OPTIONS', 'TRACE'];
        if (!safeMethods.includes(options.method || 'GET')) {
            const csrftoken = getCookie('csrftoken');
            if (csrftoken) {
                headers['X-CSRFToken'] = csrftoken;
            }
        }

        if (options.body && !(options.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }

        // Sanitize URL to remove credentials if present (fixes Basic Auth issue on staging)
        try {
            const base = window.location.origin;
            const urlObj = new URL(urlStr, base);
            if (urlObj.username || urlObj.password) {
                urlObj.username = '';
                urlObj.password = '';
                urlStr = urlObj.toString();
            }
        } catch (e) {
        }

        try {
            const response = await fetch(urlStr, { ...options, headers });

            if (response.status === 401) {
                const newAuthToken = await refreshAuthToken();
                if (newAuthToken) {
                    headers['Authorization'] = `Bearer ${newAuthToken}`;
                    return await fetch(urlStr, { ...options, headers });
                }
            }
            return response;
        } catch (fetchErr) {
            throw fetchErr;
        }
    }, [refreshAuthToken]); 
    // Actually login/logout are defined inside component, so we should include them or use refs.
    // In original code they were deps.

    const authValue = useMemo(() => ({ authToken, user, loading, login, logout, fetchWithAuth }), [authToken, user, loading, fetchWithAuth]);

    return <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>;
};
